import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Box,
  Stack,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { getAllHolidays, deleteHoliday } from '../../services/companyHolidayService';
import Iconify from '../../components/iconify';
import HolidayAddEditModel from './HolidayAddEditModel';
import { DeleteModal } from '../clients/components';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import { useStyles } from './holidayListPageStyles';
import { isAdmin, isHR, isManager, isSuperAdmin } from '../../utils/share';

// Utility function to check if a date is a weekend
const isWeekend = (date) => {
  const day = format(new Date(date), 'EEEE');
  return day === 'Saturday' || day === 'Sunday';
};

const isUpcoming = (date) => {
  const today = new Date();
  const holidayDate = new Date(date);
  return holidayDate >= today;
};

export default function HolidayListPage() {
  const classes = useStyles();
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Holidays', path: '' },
  ];

  const isAdminFlag = isAdmin() || isSuperAdmin() || isHR() || isManager();
  const [isLoading, setIsLoading] = useState(false);
  const [holidayList, setHolidayList] = useState([]);
  const [open, setOpen] = useState(false);
  const [holidayToEdit, setHolidayToEdit] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);

  useEffect(() => {
    fetchHolidayData();
  }, []);

  const NoHolidaysComponent = () => (
    <>
      <Paper className={classes.noHolidays}>
        <Typography variant="h6" color="text.secondary" className={classes.message}>
          Oops! Can't find any holidays. 😞
        </Typography>
        {isAdminFlag && (
          <Typography variant="body1" color="text.secondary" className={classes.infoMessage}>
            You can add holidays by clicking the "Add" button.
          </Typography>
        )}
      </Paper>
    </>
  );

  const fetchHolidayData = async () => {
    setIsLoading(true);
    try {
      const response = await getAllHolidays();
      setHolidayList(response?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setHolidayToEdit(null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleAddHoliday = (newHoliday) => {
    setHolidayList([...holidayList, newHoliday]);
    fetchHolidayData();
  };

  const handleUpdateHoliday = (updatedHoliday) => {
    setHolidayList(holidayList.map((holiday) => (holiday.id === updatedHoliday.id ? updatedHoliday : holiday)));
    fetchHolidayData();
  };

  const handleEdit = (holiday) => {
    setHolidayToEdit(holiday);
    setOpen(true);
  };

  const handleDeleteOpen = (holiday) => {
    setHolidayToDelete(holiday);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setHolidayToDelete(null);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteHoliday(holidayToDelete.id);
      setHolidayList(holidayList.filter((holiday) => holiday.id !== holidayToDelete.id));
      toast.success(response?.data?.message);
      handleDeleteClose();
      fetchHolidayData();
    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="4xl">
      <Box className={classes.container}>
        <BreadCrumb breadcrumbs={breadcrumbs} />

        {isAdminFlag && (
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleOpen} size="large">
              Add New Holiday
            </Button>
          </Stack>
        )}

        {holidayList.length === 0 ? (
          <NoHolidaysComponent />
        ) : (
          holidayList.map((yearData) => {
            const year = Object.keys(yearData)[0];
            const holidays = yearData[year];

            return (
              <React.Fragment key={year}>
                {isAdminFlag ? (
                  <>
                    <Typography variant="h5" gutterBottom>
                      {year}
                    </Typography>
                    <Divider />
                  </>
                ) : (
                  <></>
                )}
                <TableContainer component={Paper} sx={{ mt: 3, mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCell}>Sr No</TableCell>
                        <TableCell className={classes.headerCell}>Festival Name</TableCell>
                        <TableCell className={classes.headerCell}>Date</TableCell>
                        <TableCell className={classes.headerCell}>Day</TableCell>
                        {isAdminFlag && <TableCell className={classes.headerCell}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {holidays.map((holiday, index) => {
                        const isWeekendHoliday = isWeekend(holiday.date);
                        const isUpcomingHoliday = isUpcoming(holiday.date);

                        return (
                          <TableRow key={holiday.id} className={isWeekendHoliday ? classes.weekendRow : ''}>
                            <TableCell>
                              {index + 1}
                              {isUpcomingHoliday && <span className={classes.upcomingIndicator} />}
                            </TableCell>
                            <TableCell>{holiday.name}</TableCell>
                            <TableCell>{format(new Date(holiday.date), 'dd-MMM-yyyy')}</TableCell>
                            <TableCell>{format(new Date(holiday.date), 'EEEE')}</TableCell>
                            {isAdminFlag && (
                              <TableCell>
                                <IconButton color="primary" onClick={() => handleEdit(holiday)} size="small">
                                  <Iconify icon="eva:edit-fill" />
                                </IconButton>
                                <IconButton color="error" onClick={() => handleDeleteOpen(holiday)} size="small">
                                  <Iconify icon="eva:trash-2-outline" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </React.Fragment>
            );
          })
        )}

        <HolidayAddEditModel
          open={open}
          onClose={handleClose}
          onAddHoliday={handleAddHoliday}
          onUpdateHoliday={handleUpdateHoliday}
          holidayToEdit={holidayToEdit}
        />

        <DeleteModal open={deleteOpen} onClose={handleDeleteClose} onDelete={handleDelete} />
      </Box>
    </Container>
  );
}
